import { gql, useLazyQuery } from '@apollo/client'

import agentClient from '@/lib/apollo/client/agent'
import type { WrappedUseLazyQuery } from '@/lib/apollo/types'

import type { ReferenceTier } from './get-applicant'

const getAllSubjectsQuery = gql`
  query (
    $customerId: String!
    $filter: OutcomeReviewFilterOptions!
    $limit: Int!
    $offset: Int!
  ) {
    getSubjects(
      customerId: $customerId
      filter: $filter
      limit: $limit
      offset: $offset
    ) {
      limit
      offset
      total
      results {
        customerId
        applicationId
        subjectId
        customerClientName
        name
        email
        avatar
        type
        journeyType
        referenceTier
        address {
          nameOrNumber
          line1
          line2
          city
          country
          postcode
        }
        milestones {
          createdAt
          event
        }
        tenancyStartDate
        visitors
        journeyConfig {
          chaseType
          referenceGroup
        }
      }
    }
  }
`

export type QueueSettings = {
  onlyShowWorkingQueue?: boolean
  includeAgentUploadedDocumentsToday?: boolean
  includeApplicantUploadedDocumentsToday?: boolean
}

export type SwimLaneGroup = {
  a: boolean
  b: boolean
  c: boolean
  other: boolean
}

export type Section = {
  needsReview?: boolean
  reOpened?: boolean
}

export type IdentitySection = {
  scottishWelsh?: Section
  other?: Section
}

export type IncomeSection = {
  paye?: Section
  other?: Section
}

enum TenancyStartDate {
  all = 'all',
  within1Day = 'within1Day',
  within2Days = 'within2Days',
  within3Days = 'within3Days',
  within5Days = 'within5Days',
  within10Days = 'within10Days',
}

export type SLA = {
  onlyShowMissedSLALastWorkingDay: boolean
  onlyShowMissedSLA2DaysInARow: boolean
  onlyShowMissedSLAAnyTime: boolean
}

export type AdditionalInfo = {
  onlyShowAdditionalInfoLastWorkingDay: boolean
}

enum AgeOfReference {
  ALL = 'all',
  TODAY = 'today',
  ONE_DAY_OLD = 'oneDayOld',
  TWO_DAYS_OLD = 'twoDaysOld',
  THREE_DAYS_OLD = 'threeDaysOld',
  FOUR_DAYS_OLD = 'fourDaysOld',
  FIVE_DAYS_OLD = 'fiveDaysOld',
  MORE_THAN_FIVE_DAYS_OLD = 'moreThanFiveDaysOld',
}

export type Fraud = {
  notFlagged: boolean
  potentialFraud: boolean
}

export type ApplicantType = {
  tenant: boolean
  guarantor: boolean
}

export type Product = {
  essential: boolean
  priority: boolean
  pro: boolean
  corporate: boolean
}

export type ReferenceGroup = {
  lomond: boolean
  ers: boolean
  other: boolean
}

export type AwaitingResponse = {
  documentRequest?: boolean
  employerReference?: boolean
  openBankingRequest?: boolean
}

export type NoOwner = {
  onlyShowNoOwner?: boolean
}

export type Filters = {
  queueSettings?: QueueSettings
  identity?: IdentitySection
  credit?: Section
  income?: IncomeSection
  residential?: Section
  applicantType: ApplicantType
  product: Product
  noOwner?: NoOwner
  referenceGroup: ReferenceGroup
  swimLaneGroup: SwimLaneGroup
  fraud: Fraud
  tenancyStartDate: TenancyStartDate
  sla: SLA
  additionalInfo: AdditionalInfo
  ageOfReference: AgeOfReference
}

export type QueryVars = {
  customerId: string
  filter: Filters
  limit: number
  offset: number
}

enum JourneyType {
  essential = 'essential',
  employer = 'employer',
  advanced = 'goodlord_advanced',
  advanced_unmapped = 'goodlord-advanced',
  corporate = 'corporate',
}

enum ChaseType {
  Default = 'default',
  AdvancedPlus = 'plus',
  Priority = 'priority',
}

type Address = {
  nameOrNumber?: string
  line1?: string
  line2?: string
  city?: string
  country?: string
  postcode?: string
}

type JourneyConfig = {
  chaseType?: ChaseType
  referenceGroup?: string
}

type Milestone = {
  event: string
  createdAt: string
}

export type Subject = {
  avatar: string
  customerId: string
  applicationId: string
  subjectId: string
  customerClientName: string
  name: string
  email: string
  type: string
  journeyType: JourneyType
  referenceTier: ReferenceTier
  address: Address
  milestones: Milestone[]
  tenancyStartDate: string
  visitors: string[]
  journeyConfig: JourneyConfig
}

export type GetAllSubjectsData = {
  getSubjects: {
    total: number
    offset: number
    results: Subject[]
  }
}

export const useGetAllSubjectsQuery: WrappedUseLazyQuery<
  GetAllSubjectsData,
  QueryVars
> = options =>
  useLazyQuery<GetAllSubjectsData, QueryVars>(getAllSubjectsQuery, {
    ...options,
    client: agentClient,
  })
